import * as XLSX from 'xlsx';

// ─── Tally ledger names ───────────────────────────────────────────────────────
// These must match the ledger names in the Tally company EXACTLY (or Tally must
// be allowed to auto-create them on import). Change here if the CA renames them.
const LEDGER = {
  EVENING: 'Evening Cruise Sales',   // Sunset & Sundown (18%)
  NIGHT:   'Night Cruise Sales',     // Dinner cruise component (18%)
  BUFFET:  'Buffet Dinner Sales',    // Dinner food component (5%)
  CGST:    'Output CGST',
  SGST:    'Output SGST',
};
const VOUCHER_TYPE = 'Sales';
const GST_HEADER = 'GSTIN';

// The exact Tally "Accounting Voucher" template headers — order and spelling
// are load-bearing for the import mapping. NOTE the trailing space in the last
// header ('Change Mode ') — it is part of the template; do not trim it.
export const TALLY_HEADERS = [
  'Voucher Date',
  'Voucher Type Name',
  'Voucher Number',
  GST_HEADER,
  'Buyer/Supplier - Address',
  'Buyer/Supplier - Pincode',
  'Ledger Name',
  'Ledger Amount',
  'Ledger Amount Dr/Cr',
  'Item Name',
  'Billed Quantity',
  'Item Rate',
  'Item Rate per',
  'Item Amount',
  'Change Mode ',
];

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// invoice_date is stored as ISO YYYY-MM-DD (set from <input type="date"> in
// InvoiceModal); Tally and the rest of this app display DD-MM-YYYY.
const toDisplayDate = (isoDate) => {
  if (!isoDate) return '';
  const [y, m, d] = String(isoDate).split('-');
  if (!y || !m || !d) return String(isoDate);
  return `${d}-${m}-${y}`;
};

// Sum of the six stored tax components. Exported so the screen can run the
// same balance check it previews as the export applies.
export function componentsSum(inv) {
  return (
    (Number(inv.taxable_amount_cruise) || 0) +
    (Number(inv.cgst_cruise) || 0) +
    (Number(inv.sgst_cruise) || 0) +
    (Number(inv.taxable_amount_food) || 0) +
    (Number(inv.cgst_food) || 0) +
    (Number(inv.sgst_food) || 0)
  );
}

export function isBalanced(inv) {
  return Math.abs(componentsSum(inv) - (Number(inv.grand_total) || 0)) <= 0.005;
}

// A ledger-only (accounting) voucher line: the five item columns and the
// change-mode column stay blank in this format. GSTIN, address, and pincode
// are set on the party (Dr) row only.
const line = (voucherDate, voucherType, voucherNumber, gstin, address, pincode, ledgerName, amount, drCr) => [
  voucherDate, voucherType, voucherNumber, gstin, address, pincode,
  ledgerName, round2(amount), drCr,
  '', '', '', '', '', '',
];

// ─── Voucher construction ─────────────────────────────────────────────────────
// Each balanced invoice becomes one voucher block (sales lines then tax):
//   Dr  party ledger        grand_total
//   Cr  cruise sales        taxable_amount_cruise   (Night vs Evening by cruise_type)
//   Cr  Buffet Dinner Sales taxable_amount_food     (dinner only)
//   Cr  Output CGST         cgst_cruise (+ cgst_food for dinner)
//   Cr  Output SGST         sgst_cruise (+ sgst_food for dinner)
// Voucher Date / Type / Number / GSTIN / Address / Pincode appear only on the first (Dr) row —
// Tally carries them down through the block. Amounts are positive; the Dr/Cr
// column carries the sign meaning.
export function buildVoucherRows(invoices) {
  const rows = [];
  const excluded = [];
  const partySet = new Set();

  (invoices || []).forEach((inv) => {
    const sum = componentsSum(inv);
    const grandTotal = Number(inv.grand_total) || 0;
    const diff = round2(sum - grandTotal);

    if (Math.abs(sum - grandTotal) > 0.005) {
      excluded.push({
        invoice_number: inv.invoice_number,
        grand_total: round2(grandTotal),
        componentsSum: round2(sum),
        diff,
      });
      return;
    }

    const isDinner = String(inv.cruise_type || '').toLowerCase().includes('dinner');
    const party = inv.partner_legal_name || '';
    if (party) partySet.add(party);

    rows.push(line(
      toDisplayDate(inv.invoice_date), VOUCHER_TYPE, inv.invoice_number,
      inv.partner_gstin || '', inv.partner_address || '', inv.partner_pincode || '', party, grandTotal, 'Dr'
    ));
    rows.push(line('', '', '', '', '', '', isDinner ? LEDGER.NIGHT : LEDGER.EVENING, inv.taxable_amount_cruise, 'Cr'));
    if (isDinner) {
      rows.push(line('', '', '', '', '', '', LEDGER.BUFFET, inv.taxable_amount_food, 'Cr'));
    }
    // Dinner keeps the two sales ledgers separate but combines cruise + food
    // tax into a single CGST line and a single SGST line.
    const cgst = isDinner ? round2((Number(inv.cgst_cruise) || 0) + (Number(inv.cgst_food) || 0)) : inv.cgst_cruise;
    const sgst = isDinner ? round2((Number(inv.sgst_cruise) || 0) + (Number(inv.sgst_food) || 0)) : inv.sgst_cruise;
    rows.push(line('', '', '', '', '', '', LEDGER.CGST, cgst, 'Cr'));
    rows.push(line('', '', '', '', '', '', LEDGER.SGST, sgst, 'Cr'));
  });

  return {
    rows,
    excluded,
    partyLedgers: Array.from(partySet).sort((a, b) => a.localeCompare(b)),
  };
}

// ─── Excel export ─────────────────────────────────────────────────────────────
export function exportTallyExcel(invoices, filenameBase) {
  const { rows, excluded, partyLedgers } = buildVoucherRows(invoices);
  const exportedCount = (invoices || []).length - excluded.length;

  const ws = XLSX.utils.aoa_to_sheet([TALLY_HEADERS, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Accounting Voucher');
  XLSX.writeFile(wb, `${filenameBase}.xlsx`);

  return { exportedCount, excluded, partyLedgers };
}
